import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { RefreshCw, ExternalLink, CheckCircle2, XCircle, Clock, MailX } from 'lucide-react';

const STATUS_MAP = {
  sent: { icon: CheckCircle2, color: 'text-emerald-600', label: 'Sent' },
  failed: { icon: XCircle, color: 'text-red-600', label: 'Failed' },
  skipped: { icon: MailX, color: 'text-slate-500', label: 'Skipped' },
  pending: { icon: Clock, color: 'text-amber-600', label: 'Pending' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const EmailsPanel = () => {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, by_type: {} });
  const [mailchimp, setMailchimp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', email_type: '', recipient: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const [emailsRes, statsRes, mcRes] = await Promise.all([
        axios.get(`${API}/admin/emails?${params.toString()}`),
        axios.get(`${API}/admin/emails/stats`),
        axios.get(`${API}/admin/mailchimp-status`),
      ]);
      setEmails(emailsRes.data.emails || []);
      setStats(statsRes.data);
      setMailchimp(mcRes.data);
    } catch (e) {
      toast.error('Could not load email log');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const total = Object.values(stats.by_status || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="emails-panel">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="surface-card"><CardContent className="pt-5 pb-4 px-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Sent</p>
          <p className="text-2xl font-bold text-foreground mt-1" data-testid="stat-total">{total}</p>
        </CardContent></Card>
        {['sent', 'failed', 'skipped', 'pending'].map(s => {
          const { color, label } = STATUS_MAP[s];
          return (
            <Card key={s} className="surface-card"><CardContent className="pt-5 pb-4 px-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${color}`} data-testid={`stat-${s}`}>{stats.by_status?.[s] || 0}</p>
            </CardContent></Card>
          );
        })}
      </div>

      {/* Mailchimp status */}
      {mailchimp && (
        <Card className="surface-card border-l-4 border-purple-400" data-testid="mailchimp-status-card">
          <CardContent className="pt-4 pb-4 px-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mailchimp Sync</p>
                <p className="text-sm mt-1">
                  {mailchimp.configured
                    ? <>Connected to <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{mailchimp.server_prefix}</code> / list <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{mailchimp.list_id}</code></>
                    : <span className="text-red-600">Not configured — set MAILCHIMP_API_KEY, MAILCHIMP_LIST_ID, MAILCHIMP_SERVER_PREFIX in backend/.env</span>}
                </p>
              </div>
              <div className="flex gap-4 text-sm">
                <div><span className="text-emerald-600 font-bold">{mailchimp.leads_synced}</span> <span className="text-muted-foreground">synced</span></div>
                <div><span className="text-amber-600 font-bold">{mailchimp.leads_unsubscribed}</span> <span className="text-muted-foreground">unsubscribed</span></div>
                <div><span className="text-red-600 font-bold">{mailchimp.leads_errored}</span> <span className="text-muted-foreground">errors</span></div>
                <div><span className="font-bold">{mailchimp.leads_total}</span> <span className="text-muted-foreground">total leads</span></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          data-testid="email-filter-recipient"
          placeholder="Filter by recipient email..."
          value={filters.recipient}
          onChange={e => setFilters({ ...filters, recipient: e.target.value })}
          className="max-w-xs"
        />
        <select
          data-testid="email-filter-status"
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-2 border border-input rounded-md bg-background text-sm"
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <select
          data-testid="email-filter-type"
          value={filters.email_type}
          onChange={e => setFilters({ ...filters, email_type: e.target.value })}
          className="px-3 py-2 border border-input rounded-md bg-background text-sm"
        >
          <option value="">All types</option>
          {Object.keys(stats.by_type || {}).map(t => (
            <option key={t} value={t}>{t} ({stats.by_type[t]})</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-left px-4 py-2 font-semibold">Sent At</th>
              <th className="text-left px-4 py-2 font-semibold">Type</th>
              <th className="text-left px-4 py-2 font-semibold">Recipient</th>
              <th className="text-left px-4 py-2 font-semibold">Subject</th>
              <th className="text-left px-4 py-2 font-semibold">Resend ID</th>
            </tr>
          </thead>
          <tbody data-testid="email-log-tbody">
            {emails.length === 0 && !loading && (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No emails logged yet.</td></tr>
            )}
            {emails.map((e, i) => {
              const s = STATUS_MAP[e.status] || STATUS_MAP.pending;
              const Icon = s.icon;
              // Stable composite key: sent_at + resend_id (or index fallback for skipped/pending)
              const rowKey = `${e.sent_at || 'na'}-${e.resend_id || e.to || i}`;
              return (
                <tr key={rowKey} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${s.color}`} title={e.error || ''}>
                      <Icon className="w-3.5 h-3.5" /> {s.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs whitespace-nowrap text-muted-foreground">{fmtDate(e.sent_at)}</td>
                  <td className="px-4 py-2 text-xs"><code className="bg-muted px-1.5 py-0.5 rounded">{e.email_type || '—'}</code></td>
                  <td className="px-4 py-2 truncate max-w-[200px]" title={e.to}>{e.to}</td>
                  <td className="px-4 py-2 truncate max-w-[280px]" title={e.subject}>{e.subject}</td>
                  <td className="px-4 py-2 text-xs">
                    {e.resend_id ? (
                      <a href="https://resend.com/emails" target="_blank" rel="noreferrer"
                         className="text-cyan-600 hover:underline inline-flex items-center gap-1"
                         title={e.resend_id}>
                        {e.resend_id.slice(0, 8)}... <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : e.error ? (
                      <span className="text-red-600 text-xs truncate max-w-[200px] block" title={e.error}>{e.error.slice(0, 40)}</span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmailsPanel;
